// One chat_messages row, shaped by its role (07-chat.md §2.7/§2.8):
// user/assistant text, a collapsed "tool used" line, a ConfirmationCard, or
// a system notice. Assistant text is Markdown (components/markdown.tsx);
// what a person typed stays plain text.

import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { chatApi } from '../../lib/api-client'
import { ErrorNote } from '../ui'
import type { ChatMessage } from '@goblic/shared'
import { ConfirmationCard } from './confirmation-card'
import { Markdown } from '../markdown'

function Bubble({
  tone,
  children,
  markdown = false,
}: {
  tone: 'user' | 'assistant'
  children: React.ReactNode
  /** Markdown content renders its own line breaks; plain text keeps the typed ones. */
  markdown?: boolean
}) {
  return (
    <div className={`flex ${tone === 'user' ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`max-w-[85%] ${markdown ? 'break-words' : 'whitespace-pre-wrap'} rounded-control px-3.5 py-2.5 text-base ${
          tone === 'user'
            ? 'bg-moss text-on-action'
            : 'bg-sunken text-ink'
        }`}
      >
        {children}
      </div>
    </div>
  )
}

export function ToolLine({ message }: { message: ChatMessage }) {
  const [open, setOpen] = useState(false)
  const details = useQuery({
    queryKey: ['chat-message', message.id, message.updatedAt],
    queryFn: () => chatApi.getMessage(message.conversationId, message.id),
    enabled: open && message.truncated === true,
  })
  const payload = (details.data ?? message).payload as { args?: unknown; result?: unknown; status?: string; isError?: boolean }
  const failed = payload.status === 'error' || payload.isError === true
  const label = toolName(message)
  return (
    <div className="text-sm text-ink-3">
      <button
        type="button"
        className="inline-flex items-center gap-1.5 hover:text-ink"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        <span>{open ? '▾' : '▸'}</span>
        <span>
          Used <span className="font-mono">{label}</span>
          {failed ? ' — failed' : ''}
        </span>
      </button>
      {open && details.isLoading ? <p role="status">Loading tool details…</p> : null}
      {open && details.isError ? <ErrorNote error={details.error} what="tool details" /> : null}
      {open && !details.isLoading && !details.isError ? (
        <pre className="mt-1.5 max-h-40 overflow-auto rounded-control bg-ink/5 p-2.5 font-mono text-xs text-ink-2">
          {JSON.stringify({ args: payload.args, result: payload.result }, null, 2)}
        </pre>
      ) : null}
    </div>
  )
}

/** "mcp__goblic__list_tasks" → "list_tasks": the action name is what says something. */
function toolName(m: ChatMessage): string {
  // Chat rows carry `tool` (+ optional human `title`); run-event rows carry `name`.
  const p = m.payload as { tool?: string; name?: string; title?: string } | null
  if (p?.title) return p.title
  const name = p?.tool ?? p?.name ?? 'Tool'
  return name.replace(/^mcp__[^_]+(?:_[^_]+)*?__/, '')
}

/** Distinct tool names in call order, repeats as "name ×n", capped so the line stays short. */
function summarizeTools(messages: ChatMessage[], max = 3): string {
  const counts = new Map<string, number>()
  for (const m of messages) counts.set(toolName(m), (counts.get(toolName(m)) ?? 0) + 1)
  const parts = [...counts].map(([name, n]) => (n > 1 ? `${name} ×${n}` : name))
  return parts.length > max ? `${parts.slice(0, max).join(', ')} +${parts.length - max}` : parts.join(', ')
}

/** Two or more consecutive tool calls fold into one "Used a, b ×2" line; each call stays inspectable inside. */
export function ToolGroup({ messages }: { messages: ChatMessage[] }) {
  const [open, setOpen] = useState(false)
  if (messages.length === 1) return <ToolLine message={messages[0]} />
  const failed = messages.filter((m) => (() => { const p = m.payload as { status?: string; isError?: boolean } | null; return p?.status === 'error' || p?.isError === true })()).length
  return (
    <div className="text-sm text-ink-3">
      <button
        type="button"
        className="inline-flex items-center gap-1.5 hover:text-ink"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        <span>{open ? '▾' : '▸'}</span>
        <span>
          Used <span className="font-mono">{summarizeTools(messages)}</span>
          {failed > 0 ? ` — ${failed} failed` : ''}
          {failed > 0 ? ` — ${failed} failed` : ''}
        </span>
      </button>
      {open ? (
        <div className="mt-1.5 space-y-1.5 border-l border-ink/10 pl-3">
          {messages.map((m) => (
            <ToolLine key={m.id} message={m} />
          ))}
        </div>
      ) : null}
    </div>
  )
}

function MessageContent({
  conversationId,
  message,
}: {
  conversationId: string
  message: ChatMessage
}) {
  switch (message.role) {
    case 'user':
      return <Bubble tone="user">{message.content}</Bubble>
    case 'assistant': {
      // chat-stream.service.ts UPDATEs the same row as deltas land, flipping
      // `streaming` to false (or dropping it) once the turn finishes — the
      // caret is purely presentational, content may still be '' right after
      // the row is created and before the first delta arrives.
      const streaming = (message.payload as { streaming?: boolean } | null)?.streaming === true
      // A text block that never got any text (the model went straight to a tool call) leaves nothing to show.
      if (!streaming && !message.content.trim()) return null
      return (
        <Bubble tone="assistant" markdown>
          <Markdown>{message.content}</Markdown>
          {streaming ? (
            <span className="animate-pulse" aria-hidden>
              ▍
            </span>
          ) : null}
        </Bubble>
      )
    }
    case 'tool':
      return <ToolLine message={message} />
    case 'confirmation':
      return <ConfirmationCard conversationId={conversationId} message={message} />
    case 'notice': {
      // Set by chat/turn-handler.ts only for a genuine failure, never for a
      // user-requested cancel or a plain status line (e.g. the oversized-
      // session notice) — those stay neutral.
      const isError = (message.payload as { level?: string } | null)?.level === 'error'
      return (
        <p className={`text-center text-sm ${isError ? 'text-danger' : 'text-ink-3'}`}>
          {message.content}
        </p>
      )
    }
    default:
      return null
  }
}

/** Preview expansion is explicit; normal rendering never fetches large bodies. */
export function MessageItem({ conversationId, message }: { conversationId: string; message: ChatMessage }) {
  const [expanded, setExpanded] = useState(false)
  const details = useQuery({
    queryKey: ['chat-message', message.id, message.updatedAt],
    queryFn: () => chatApi.getMessage(conversationId, message.id),
    enabled: expanded && message.truncated === true && message.payload.streaming !== true,
  })
  return <div>
    <MessageContent conversationId={conversationId} message={details.data ?? message} />
    {message.truncated && message.role !== 'tool' ? <button className="btn-ghost btn-sm" disabled={details.isFetching || message.payload.streaming === true || !!details.data} onClick={() => setExpanded(true)}>{message.payload.streaming === true ? 'Full reply available when finished' : details.isFetching ? 'Loading…' : details.data ? 'Full message loaded' : 'Load full message'}</button> : null}
    {details.isError ? <ErrorNote error={details.error} what="message details" /> : null}
  </div>
}
